import { useCallback } from 'react';
import { BoardGameState, PieceOnPosition } from './useBoardState';

interface UseBoardInteractionsProps {
  gameState: BoardGameState;
  onSquareClick: (position: number) => void;
  onPieceClick?: (pieceIndex: number) => void;
}

export const useBoardInteractions = ({
  gameState,
  onSquareClick,
  onPieceClick,
}: UseBoardInteractionsProps) => {
  // Handle clicking on a path cell
  const handleCellClick = useCallback(
    (position: number, piecesOnPosition: PieceOnPosition[]) => {
      if (position >= 0) {
        if (piecesOnPosition.length > 0 && onPieceClick) {
          // Check if current player exists
          const currentPlayer = gameState.players[gameState.currentPlayer];
          if (currentPlayer) {
            // If there's a piece here, try to click it
            const currentPlayerPiece = piecesOnPosition.find(
              (p) => p.player.id === currentPlayer.id
            );
            if (currentPlayerPiece) {
              onPieceClick(currentPlayerPiece.pieceIndex);
              return;
            }
          }
        }
        // Fall back to square click if no piece click or no current player
        onSquareClick(position);
      }
    },
    [gameState.players, gameState.currentPlayer, onSquareClick, onPieceClick]
  );

  // Handle clicking on a piece in home area
  const handleHomePieceClick = useCallback(
    (piece: { position: number }, pieceIndex: number) => {
      if (piece.position === -1 && onPieceClick) {
        onPieceClick(pieceIndex);
      }
    },
    [onPieceClick]
  );

  // Handle clicking on a piece on the board
  const handleBoardPieceClick = useCallback(
    (e: React.MouseEvent, playerId: string, pieceIndex: number) => {
      e.stopPropagation();

      // Check if current player exists
      const currentPlayer = gameState.players[gameState.currentPlayer];
      if (onPieceClick && currentPlayer && playerId === currentPlayer.id) {
        onPieceClick(pieceIndex);
      }
    },
    [onPieceClick, gameState.players, gameState.currentPlayer]
  );

  return {
    handleCellClick,
    handleHomePieceClick,
    handleBoardPieceClick,
  };
};
